import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const SORTS = [
  ["featured", "Featured"], ["best_selling", "Best Selling"], ["newest", "Newest"],
  ["price_asc", "Price: Low → High"], ["price_desc", "Price: High → Low"], ["rating", "Highest Rated"],
];
const USE_CASES = ["Everyday Carry", "Outdoor", "Preparedness", "Home Security", "Vehicle", "Range", "Personal Security"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ products: [], total: 0, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "featured";
  const filter = params.get("filter") || "";
  const goodFor = params.get("good_for") || "";
  const maxPrice = params.get("max_price") || "";
  const subCategory = params.get("sub_category") || "";
  const page = parseInt(params.get("page") || "1");

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get(`/subcategories${category ? `?category=${category}` : ""}`).then(({ data }) => setSubcats(data)).catch(() => {});
  }, [category]);

  const queryString = useMemo(() => {
    const q = new URLSearchParams({ sort, page: String(page), limit: "24" });
    if (category) q.set("category", category);
    if (search) q.set("search", search);
    if (goodFor) q.set("good_for", goodFor);
    if (maxPrice) q.set("max_price", maxPrice);
    if (subCategory) q.set("sub_category", subCategory);
    if (filter === "new") q.set("new_arrival", "true");
    if (filter === "sale") q.set("on_sale", "true");
    if (filter === "best") q.set("best_seller", "true");
    return q.toString();
  }, [category, search, sort, filter, goodFor, maxPrice, subCategory, page]);

  useEffect(() => {
    setLoading(true);
    api.get(`/products?${queryString}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [queryString]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === "category") next.delete("sub_category");
    next.delete("page");
    setParams(next);
  };

  const clearFilters = () => setParams(new URLSearchParams());
  const hasFilters = !!(category || search || filter || goodFor || maxPrice || subCategory);

  const catName = categories.find((c) => c.slug === category)?.name;

  const Filters = () => (
    <div className="space-y-8">
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Category</h4>
        <button data-testid="filter-cat-all" onClick={() => setParam("category", "")} className={`block text-sm py-1 ${!category ? "text-[#4A5D23] font-bold" : "text-black/60 hover:text-black"}`}>All Gear</button>
        {categories.map((c) => (
          <button key={c.slug} data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.slug)}
            className={`block text-sm py-1 text-left ${category === c.slug ? "text-[#4A5D23] font-bold" : "text-black/60 hover:text-black"}`}>{c.name}</button>
        ))}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Quick Filters</h4>
        {[["", "All"], ["best", "Best Sellers"], ["new", "New Arrivals"], ["sale", "On Sale"]].map(([v, l]) => (
          <button key={l} onClick={() => setParam("filter", v)} className={`block text-sm py-1 ${filter === v ? "text-[#4A5D23] font-bold" : "text-black/60 hover:text-black"}`}>{l}</button>
        ))}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Use Case</h4>
        {USE_CASES.map((u) => (
          <button key={u} data-testid={`filter-usecase-${u.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setParam("good_for", goodFor === u ? "" : u)} className={`block text-sm py-1 ${goodFor === u ? "text-[#4A5D23] font-bold" : "text-black/60 hover:text-black"}`}>{u}</button>
        ))}
      </div>
      {subcats.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Product Type</h4>
          <div className="max-h-64 overflow-y-auto pr-2">
            {subcats.map((sc) => (
              <button key={sc.name} data-testid={`filter-subcat-${sc.name.replace(/\W+/g, "-").toLowerCase()}`}
                onClick={() => setParam("sub_category", subCategory === sc.name ? "" : sc.name)}
                className={`block text-sm py-1 text-left ${subCategory === sc.name ? "text-[#4A5D23] font-bold" : "text-black/60 hover:text-black"}`}>
                {sc.name} <span className="text-black/30 text-xs">({sc.count})</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Max Price</h4>
        {[["", "Any"], ["25", "Under $25"], ["50", "Under $50"], ["100", "Under $100"]].map(([v, l]) => (
          <button key={l} onClick={() => setParam("max_price", v)} className={`block text-sm py-1 ${maxPrice === v ? "text-[#4A5D23] font-bold" : "text-black/60 hover:text-black"}`}>{l}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">Shop</p>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold mt-1">
          {search ? `Results for "${search}"` : catName || "All Gear"}
        </h1>
        <p className="text-sm text-black/50 mt-1" data-testid="shop-result-count">{data.total} products</p>
      </div>
      <div className="flex gap-10">
        <aside className="hidden lg:block w-56 shrink-0"><Filters /></aside>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-6 gap-3">
            <button data-testid="mobile-filters-btn" onClick={() => setFiltersOpen(true)} className="lg:hidden flex items-center gap-2 border border-black/20 px-4 py-2 text-xs font-bold uppercase tracking-widest">
              <SlidersHorizontal size={14} /> Filters
            </button>
            <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
              className="border border-black/20 px-3 py-2 text-xs font-semibold uppercase tracking-wider bg-white focus:outline-none focus:ring-2 focus:ring-[#4A5D23] ml-auto">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#F4F3F0] animate-pulse" />)}
            </div>
          ) : data.products.length === 0 ? (
            <div className="py-20 text-center">
              <p data-testid="no-results" className="text-black/50">No products match your filters.</p>
              {hasFilters && (
                <button data-testid="clear-filters-btn" onClick={clearFilters}
                  className="mt-5 bg-[#121212] text-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div data-testid="shop-products-grid" className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {data.products.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          )}
          {data.pages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-10 flex-wrap">
              {(() => {
                const go = (p) => { const n = new URLSearchParams(params); n.set("page", String(p)); setParams(n); window.scrollTo(0, 0); };
                const pages = [];
                const windowPages = [];
                for (let i = Math.max(1, page - 2); i <= Math.min(data.pages, page + 2); i++) windowPages.push(i);
                if (windowPages[0] > 1) pages.push(1, windowPages[0] > 2 ? "…" : null);
                pages.push(...windowPages);
                if (windowPages[windowPages.length - 1] < data.pages) pages.push(windowPages[windowPages.length - 1] < data.pages - 1 ? "…" : null, data.pages);
                return (
                  <>
                    <button data-testid="page-prev" disabled={page <= 1} onClick={() => go(page - 1)} className="px-3 h-9 text-xs font-bold border border-black/20 disabled:opacity-30 hover:border-black">Prev</button>
                    {pages.filter((p) => p !== null).map((p, i) => p === "…" ? <span key={`e${i}`} className="text-black/40 px-1">…</span> : (
                      <button key={p} data-testid={`page-btn-${p}`} onClick={() => go(p)}
                        className={`w-9 h-9 text-xs font-bold border ${page === p ? "bg-[#121212] text-white border-black" : "border-black/20 hover:border-black"}`}>{p}</button>
                    ))}
                    <button data-testid="page-next" disabled={page >= data.pages} onClick={() => go(page + 1)} className="px-3 h-9 text-xs font-bold border border-black/20 disabled:opacity-30 hover:border-black">Next</button>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
      {filtersOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-heading font-bold uppercase">Filters</h3>
              <button onClick={() => setFiltersOpen(false)} aria-label="Close"><X size={20} /></button>
            </div>
            <Filters />
          </div>
        </div>
      )}
    </div>
  );
}
